import apiClient from './client'

export interface BackupConfiguracao {
  id: number
  id_empresa: number | null
  nome_configuracao: string
  tipo_backup: 'completo' | 'incremental'
  cron_expression: string
  ativo: boolean
  s3_bucket: string
  s3_prefix: string
  retention_days: number
  configuracao_incremental: {
    metodo: string
    wal_retention_days?: number
    basebackup_options?: Record<string, any>
  } | null
  filename: string | null
  s3_key: string | null
  size_bytes: number | null
  status: 'pendente' | 'em_progresso' | 'concluido' | 'falhou' | 'expirado'
  error_message: string | null
  ultima_execucao_at: string | null
  proxima_execucao_at: string | null
  created_by: number | null
  created_at: string
  updated_at: string | null
  expires_at: string | null
  deleted_at: string | null
  is_deleted: boolean
}

export interface BackupConfiguracaoCreate {
  id_empresa: number | null
  nome_configuracao: string
  tipo_backup: 'completo' | 'incremental'
  cron_expression: string
  ativo: boolean
  s3_bucket: string
  s3_prefix?: string
  retention_days: number
  configuracao_incremental?: {
    metodo: string
    wal_retention_days?: number
    basebackup_options?: Record<string, any>
  } | null
}

export interface BackupConfiguracaoUpdate {
  id_empresa?: number | null
  nome_configuracao?: string
  tipo_backup?: 'completo' | 'incremental'
  cron_expression?: string
  ativo?: boolean
  s3_bucket?: string
  s3_prefix?: string
  retention_days?: number
  configuracao_incremental?: {
    metodo: string
    wal_retention_days?: number
    basebackup_options?: Record<string, any>
  } | null
}

export interface BackupExecucao {
  id: number
  id_backup: number
  id_empresa: number | null
  tipo_backup: 'completo' | 'incremental'
  status: 'pendente' | 'em_progresso' | 'concluido' | 'falhou' | 'expirado'
  filename: string | null
  s3_key: string | null
  size_bytes: number | null
  error_message: string | null
  started_at: string
  completed_at: string | null
  duration_seconds: number | null
}

export interface BackupListResponse {
  backups: BackupConfiguracao[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface BackupExecucoesListResponse {
  execucoes: BackupExecucao[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface BackupSummary {
  total_backups: number
  total_size_bytes: number
  backups_ativos: number
  backups_expirados: number
  backups_falhados: number
  proximo_backup_expira: string | null
}

export interface CronExplanation {
  cron_expression: string
  explicacao: string
  proxima_execucao: string | null
  valido: boolean
  erro: string | null
}

export const backupsApi = {
  list: async (params?: {
    page?: number
    page_size?: number
    id_empresa?: number
    ativo?: boolean
    include_deleted?: boolean
  }): Promise<BackupListResponse> => {
    const response = await apiClient.get<BackupListResponse>('/backup/banco', { params })
    return response.data
  },

  get: async (id: number): Promise<BackupConfiguracao> => {
    const response = await apiClient.get<BackupConfiguracao>(`/backup/banco/${id}`)
    return response.data
  },

  create: async (data: BackupConfiguracaoCreate): Promise<BackupConfiguracao> => {
    const response = await apiClient.post<{ backup: BackupConfiguracao }>('/backup/banco', data)
    return response.data.backup
  },

  update: async (id: number, data: BackupConfiguracaoUpdate): Promise<BackupConfiguracao> => {
    const response = await apiClient.put<BackupConfiguracao>(`/backup/banco/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/backup/banco/${id}`)
  },

  executar: async (id: number): Promise<BackupExecucao> => {
    const response = await apiClient.post<BackupExecucao>(`/backup/banco/${id}/executar`)
    return response.data
  },

  explicarCron: async (id: number): Promise<CronExplanation> => {
    const response = await apiClient.get<CronExplanation>(`/backup/banco/${id}/explicar-cron`)
    return response.data
  },

  validarCron: async (cron_expression: string): Promise<CronExplanation> => {
    const response = await apiClient.post<CronExplanation>('/backup/banco/validar-cron', {
      cron_expression,
    })
    return response.data
  },

  listExecucoes: async (params?: {
    page?: number
    page_size?: number
    backup_id?: number
    id_empresa?: number
    status?: string
  }): Promise<BackupExecucoesListResponse> => {
    const response = await apiClient.get<BackupExecucoesListResponse>('/backup/execucoes', {
      params,
    })
    return response.data
  },

  getExecucao: async (id: number): Promise<BackupExecucao> => {
    const response = await apiClient.get<BackupExecucao>(`/backup/execucoes/${id}`)
    return response.data
  },

  listExecucoesPorBackup: async (
    backup_id: number,
    params?: { page?: number; page_size?: number }
  ): Promise<BackupExecucoesListResponse> => {
    const response = await apiClient.get<BackupExecucoesListResponse>(
      `/backup/execucoes/backup/${backup_id}`,
      { params }
    )
    return response.data
  },

  getResumo: async (): Promise<BackupSummary> => {
    const response = await apiClient.get<BackupSummary>('/backup/banco/resumo')
    return response.data
  },
}

